import { useEffect, useState } from 'react';
import { getGlobalActivity, DailyCount, CompanyStat } from '../services/api';

function BarChart({ data, color = '#3b82f6', height = 100 }: { data: DailyCount[]; color?: string; height?: number }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const W = 700;
  const bw = W / data.length;
  const showEvery = Math.max(1, Math.ceil(data.length / 12));
  return (
    <svg viewBox={`0 0 ${W} ${height + 24}`} style={{ width: '100%', display: 'block', overflow: 'visible' }}>
      {data.map((d, i) => {
        const bh = Math.max(1, Math.round((d.count / max) * height));
        const label = d.date.slice(5); // MM-DD
        return (
          <g key={i}>
            <rect x={i * bw + 1} y={height - (d.count > 0 ? bh : 0)} width={bw - 2} height={d.count > 0 ? bh : 1}
              fill={d.count > 0 ? color : '#f1f5f9'} rx={2}>
              <title>{`${d.date}: ${d.count}件`}</title>
            </rect>
            {i % showEvery === 0 && (
              <text x={i * bw + bw / 2} y={height + 16} textAnchor="middle" fontSize="9" fill="#94a3b8">{label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default function StatsPage() {
  const [days, setDays]         = useState(30);
  const [daily, setDaily]       = useState<DailyCount[]>([]);
  const [companies, setCompanies] = useState<CompanyStat[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    setLoading(true);
    getGlobalActivity(days)
      .then(d => { setDaily(d.daily); setCompanies(d.by_company); })
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => setLoading(false));
  }, [days]);

  const total = daily.reduce((s, d) => s + d.count, 0);
  const maxDay = daily.reduce((m, d) => d.count > m.count ? d : m, { date: '', count: 0 });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.3px' }}>利用状況</h1>
        <div style={{ display: 'flex', gap: 3, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
          {[7, 14, 30, 60].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              background: days === d ? '#fff' : 'transparent', color: days === d ? '#111827' : '#9ca3af',
              border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: days === d ? 600 : 400, cursor: 'pointer',
              boxShadow: days === d ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>{d}日</button>
          ))}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 64, color: '#94a3b8' }}>読み込み中...</div>
      ) : (
        <>
          {/* サマリー */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: `${days}日間 合計打刻`, value: total.toLocaleString(), unit: '件' },
              { label: '1日平均', value: Math.round(total / days).toLocaleString(), unit: '件/日' },
              { label: '最多打刻日', value: maxDay.count > 0 ? (maxDay.date ?? '').slice(5) : '—', unit: maxDay.count > 0 ? `${maxDay.count}件` : '' },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', letterSpacing: '-0.5px' }}>{s.value} <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>{s.unit}</span></div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* グローバルチャート */}
          <div style={{ background: '#fff', borderRadius: 10, padding: '20px 22px', border: '1px solid #e5e7eb', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>
              全社合計 打刻数推移（過去{days}日）
            </div>
            {daily.every(d => d.count === 0) ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 14 }}>この期間の打刻データがありません</div>
            ) : (
              <BarChart data={daily} height={120} color="#3b82f6" />
            )}
          </div>

          {/* 会社別ランキング */}
          <div style={{ background: '#fff', borderRadius: 10, padding: '20px 22px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16 }}>
              会社別 打刻数ランキング（過去{days}日）
            </div>
            {companies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 14 }}>データがありません</div>
            ) : (
              <div>
                {companies.map((c, i) => {
                  const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
                  const maxCount = companies[0]?.count ?? 1;
                  const barPct = maxCount > 0 ? (c.count / maxCount) * 100 : 0;
                  return (
                    <div key={c.company_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: i < 3 ? ['#f59e0b','#94a3b8','#b45309'][i] : '#cbd5e1', width: 20, textAlign: 'center' }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name}</div>
                        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, marginTop: 4 }}>
                          <div style={{ height: '100%', width: `${barPct}%`, background: '#3b82f6', borderRadius: 3 }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', minWidth: 60, textAlign: 'right' }}>
                        {c.count.toLocaleString()}<span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 3 }}>件</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', minWidth: 36, textAlign: 'right' }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
